import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { setSceneSpecifierOverride, setSceneMeta } from '../../../../store/visualizerSlice';
import StepNavigation from '../../StepNavigation/StepNavigation';
import styles from './KitchenArrangement.module.css';

// Kitchen arrangement options come from the Rooms sheet in Floorplans.xlsx.
// A plan can have multiple rows with Area="Kitchen"; each becomes an option.
// A "Label" column (e.g., "Kitchen A-1", "Kitchen A-1 Townhomes") is used to
// disambiguate options that share the same Specifier.

function thumbUrl(specifier) {
    return `https://renderfarm2.chameleonpower.com/proxy/GetImage.ashx?spec=${encodeURIComponent(specifier)}&sel=-1&type=base&size=508`;
}

function normalizeSpec(raw) {
    return String(raw || '').replace(/\\/g, '/');
}

// Fall back to the last path segment when Label is empty.
function labelFor(row) {
    const label = String(row.Label || row.label || '').trim();
    if (label) return label;
    const spec = normalizeSpec(row.Specifier || row.specifier || '');
    const tail = spec.split('/').pop() || '';
    return tail.replace(/_/g, ' ');
}

export default function KitchenArrangement({
    stepIndex,
    room = 'Kitchen',
    onViewAllApplied,
    onNext,
    onPrevious
}) {
    const dispatch = useDispatch();
    const activeFloorplanId = useSelector(state => state.visualizer.activeFloorplanId);
    const sceneMeta = useSelector(state => state.visualizer.sceneMeta);
    const rooms = useSelector(state => state.floorplan.rooms);

    // All Kitchen rows for this plan become arrangement options.
    const options = useMemo(() => {
        return (rooms || [])
            .filter(r =>
                String(r.PlanId ?? r.planId) === String(activeFloorplanId) &&
                String(r.Area || r.area || '').toLowerCase() === 'kitchen'
            )
            .map(r => {
                const label = labelFor(r);
                return {
                    key: label,
                    label,
                    specifier: normalizeSpec(r.Specifier || r.specifier)
                };
            })
            .filter(o => o.specifier);
    }, [rooms, activeFloorplanId]);

    const selectedKey = sceneMeta?.[room]?.kitchenArrangement || options[0]?.key;

    const handleSelect = (opt) => {
        dispatch(setSceneSpecifierOverride({ area: room, specifier: opt.specifier }));
        dispatch(setSceneMeta({ area: room, meta: { kitchenArrangement: opt.key } }));
    };

    return (
        <div className={styles.wrapper}>
            <StepNavigation
                title="Kitchen Arrangement"
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={onViewAllApplied}
            />

            <div className={styles.instructions}>
                Choose a kitchen arrangement to visualize.
            </div>

            {options.length === 0 && (
                <div className={styles.instructions} style={{ color: '#a33' }}>
                    No kitchen arrangements configured for this plan. Add extra
                    Kitchen rows to the Rooms sheet in Floorplans.xlsx.
                </div>
            )}

            <div className={styles.grid}>
                {options.map(opt => (
                    <button
                        key={opt.key}
                        type="button"
                        className={`${styles.card} ${selectedKey === opt.key ? styles.cardActive : ''}`}
                        onClick={() => handleSelect(opt)}
                    >
                        <img
                            src={thumbUrl(opt.specifier)}
                            alt={opt.label}
                            className={styles.thumb}
                        />
                        <div className={styles.title}>{opt.label}</div>
                    </button>
                ))}
            </div>
        </div>
    );
}

KitchenArrangement.propTypes = {
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
